import { useState } from 'react';
import { Link } from 'react-router';
import { SessionSummary } from '../components/SessionSummary';
import { EmptyBlock, ErrorBlock, LoadingBlock, PageHeader } from '../components/states';
import { downloadIcs, icsFilename, scheduleIcs } from '../lib/calendar';
import { formatDayHeading } from '../lib/datetime';
import { overlapLabel, overlapsBySession } from '../lib/overlaps';
import { sessionPath } from '../lib/paths';
import {
  useEvent,
  useMyProfile,
  useScheduleActions,
  useUpdateMyProfile,
  type CalendarSubscription,
  type Session,
} from '../lib/queries';
import { useSavedSessions } from '../lib/savedSessions';

/** SCH-01 through SCH-04: the sessions they saved, collisions flagged, and two ways into a calendar. */
export function SchedulePage() {
  const event = useEvent();
  const saved = useSavedSessions();

  if (event.isPending || saved.isPending) {
    return <LoadingBlock label="Loading your schedule…" />;
  }

  if (event.isError || !event.data || saved.isError || !saved.schedule) {
    return (
      <ErrorBlock
        message="Your schedule could not be loaded."
        onRetry={() => {
          void event.refetch();
          saved.refetch();
        }}
      />
    );
  }

  const { timezone } = event.data;
  const { days, calendar } = saved.schedule;
  const mine = days.flatMap((day) => day.sessions);
  const overlaps = overlapsBySession(mine);

  return (
    <>
      <PageHeader title="My schedule" />

      <main className="px-gutter pb-6">
        {mine.length === 0 ? (
          <EmptyBlock title="Nothing saved yet">
            Star a session on the{' '}
            <Link to="/program" className="font-semibold text-brand-text underline">
              agenda
            </Link>{' '}
            and it will wait for you here.
          </EmptyBlock>
        ) : (
          // On a laptop the two ways this schedule leaves the app move out of the way: the days
          // read down the left, and what to do with them sits beside the first one instead of
          // below the last. On a phone it is one column, in that same order.
          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_21rem] lg:items-start lg:gap-8">
            <div className="space-y-6">
              {days.map((day) => (
                <section key={day.key}>
                  <h2 className="text-eyebrow tracking-eyebrow mb-3 font-bold text-ink-3 uppercase">
                    {formatDayHeading(day.date, timezone)}
                  </h2>

                  <ol>
                    {day.sessions.map((session, index) => (
                      <li key={session.id} className="relative pb-card pl-7 last:pb-0">
                        {/* The rail is drawn per row so it stops at the last dot, not below it. */}
                        {index < day.sessions.length - 1 && (
                          <span
                            className="absolute top-6 bottom-0 left-[5px] w-px bg-border-2"
                            aria-hidden="true"
                          />
                        )}
                        <span
                          className="absolute top-[19px] left-0 size-2.5 rounded-full border-[1.5px] border-hairline bg-canvas"
                          aria-hidden="true"
                        />

                        <ScheduleCard
                          session={session}
                          timeZone={timezone}
                          clashes={overlaps.get(session.id)}
                        />
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>

            <div>
              <CalendarBlock
                calendar={calendar}
                onDownload={() =>
                  downloadIcs(
                    icsFilename(`${event.data.name} my schedule`),
                    scheduleIcs(mine, {
                      name: event.data.name,
                      venueName: event.data.venueName,
                    }),
                  )
                }
                sessionCount={mine.length}
              />

              <DigestBlock />
            </div>
          </div>
        )}
      </main>
    </>
  );
}

/**
 * The two ways a schedule leaves the app, in the order they are worth having. A subscription (SCH-04)
 * follows the program when a room moves, which is what an attendee actually wants during a
 * conference; the download (SCH-03) is the one that works when they do not want to keep a live link
 * at all.
 */
function CalendarBlock({
  calendar,
  onDownload,
  sessionCount,
}: {
  calendar: CalendarSubscription | null;
  onDownload: () => void;
  sessionCount: number;
}) {
  const { subscribe, unsubscribe } = useScheduleActions();
  const [copied, setCopied] = useState(false);

  return (
    <section className="mt-6 space-y-2 lg:mt-0">
      {calendar ? (
        <>
          <a
            href={calendar.webcalUrl}
            className="text-button rounded-button flex min-h-touch w-full items-center justify-center bg-ink px-4 font-semibold text-white"
          >
            Subscribe in my calendar
          </a>

          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(calendar.url).then(() => setCopied(true));
            }}
            className="text-button rounded-button flex min-h-touch w-full items-center justify-center border border-border px-4 font-semibold text-ink"
          >
            {copied ? 'Address copied' : 'Copy the calendar address'}
          </button>

          <p className="text-subtitle text-ink-3">
            This calendar updates itself as the program changes. Anyone with the address can read
            your schedule, so{' '}
            <button
              type="button"
              onClick={() => unsubscribe.mutate()}
              disabled={unsubscribe.isPending}
              className="font-semibold text-brand-text underline disabled:opacity-50"
            >
              turn it off
            </button>{' '}
            if you have shared it by mistake. You will get a new address next time.
          </p>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => subscribe.mutate()}
            disabled={subscribe.isPending}
            className="text-button rounded-button flex min-h-touch w-full items-center justify-center bg-ink px-4 font-semibold text-white disabled:opacity-50"
          >
            {subscribe.isPending ? 'Setting it up…' : 'Add my schedule to my calendar'}
          </button>

          <p className="text-subtitle text-ink-3">
            A calendar that stays in step with the program, so a room change reaches you without
            opening the app.
          </p>
        </>
      )}

      <button
        type="button"
        onClick={onDownload}
        className="text-subtitle min-h-touch w-full font-semibold text-ink-3 underline"
      >
        Or download {sessionCount} {sessionCount === 1 ? 'session' : 'sessions'} as a file
      </button>
    </section>
  );
}

/**
 * SCH-05, and it lives here rather than with the privacy switches because it is a third way of
 * getting this schedule somewhere other than the app — beside the subscription and the download,
 * where somebody looking for exactly that will already be looking.
 *
 * Off by default, and the copy says when it arrives rather than only what it is: "each morning of
 * the event" is the whole of what somebody needs in order to decide.
 */
function DigestBlock() {
  const profile = useMyProfile();
  const update = useUpdateMyProfile();

  // Nothing at all until the profile is known. A switch drawn off and then flipping itself on a
  // moment later reads as the app having changed their mind for them.
  if (!profile.data) {
    return null;
  }

  const on = update.isPending ? !profile.data.digestOptIn : profile.data.digestOptIn;

  return (
    <section className="mt-6">
      <label className="rounded-card flex items-center gap-3 border border-border bg-surface p-4">
        <span className="min-w-0 flex-1">
          <span className="text-row block font-semibold text-ink">Email me this each morning</span>
          <span className="text-subtitle mt-0.5 block text-ink-3">
            One message on each day of the event, listing what you saved for that day. Nothing on
            the other days.
          </span>
        </span>

        <input
          type="checkbox"
          className="peer sr-only"
          checked={on}
          disabled={update.isPending}
          onChange={(changed) => update.mutate({ digestOptIn: changed.target.checked })}
        />
        <span
          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand ${
            on ? 'bg-brand' : 'bg-sand-3'
          }`}
          aria-hidden="true"
        >
          <span
            className={`absolute top-1 size-5 rounded-full bg-white transition-all ${on ? 'left-6' : 'left-1'}`}
          />
        </span>
      </label>

      {update.isError && (
        <p className="text-subtitle mt-2 font-semibold text-alert-text" role="status">
          That did not save. Try again.
        </p>
      )}
    </section>
  );
}

function ScheduleCard({
  session,
  timeZone,
  clashes,
}: {
  session: Session;
  timeZone: string;
  clashes?: Session[];
}) {
  return (
    <article className="rounded-card relative border border-border bg-surface p-4">
      <Link
        to={sessionPath(session)}
        className="rounded-card absolute inset-0"
        aria-label={session.title}
      />

      <SessionSummary session={session} timeZone={timeZone} />

      <div className="pointer-events-none">
        {session.isCancelled ? (
          <Plate tone="alert">{session.cancellationReason ?? 'Cancelled'}</Plate>
        ) : (
          clashes && <Plate tone="warn">{overlapLabel(clashes)}</Plate>
        )}
      </div>
    </article>
  );
}

/**
 * The inset strip inside a card. The brand kit assigns the two status colours by meaning, not by
 * severity: amber is a schedule problem you can still act on, red is a session that is off.
 */
const PLATE_TONES = {
  warn: 'bg-warn-bg text-warn-text',
  alert: 'bg-alert-bg text-alert-text',
};

function Plate({ tone, children }: { tone: keyof typeof PLATE_TONES; children: string }) {
  return (
    <p
      className={`rounded-button text-subtitle mt-3 flex items-start gap-2 px-3 py-2 font-semibold ${PLATE_TONES[tone]}`}
    >
      <span
        className={`mt-1.5 size-1.5 shrink-0 rounded-full ${tone === 'warn' ? 'bg-warn' : 'bg-alert'}`}
        aria-hidden="true"
      />
      <span>{children}</span>
    </p>
  );
}
